import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';

// Import Potree source directly — Vite processes it as ES modules
// This sets window.Potree for backward compatibility
import '../potree/index';

const loadScript = async ({ src, bodyElement }: { src: string; bodyElement: HTMLBodyElement }) => {
  return new Promise((resolve, reject) => {
    let script = undefined;
    if (document.getElementById(src)) return resolve(true);
    script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.async = false;
    script.id = src;
    script.onload = function () {
      resolve(true);
    };
    script.onerror = function () {
      reject(new Error('script error'));
    };
    bodyElement.append(script);
  });
};

const loadStyleSheet = async ({ src, headElement }: { src: string; headElement: HTMLHeadElement }) => {
  return new Promise((resolve) => {
    let ready = false;
    let stylesheet = undefined;
    if (document.getElementById(src)) return resolve(true);
    stylesheet = document.createElement('link');
    stylesheet.type = 'text/css';
    stylesheet.href = src;
    stylesheet.id = src;
    stylesheet.rel = 'stylesheet';
    // @ts-expect-error - onreadystatechange is a legacy IE property not in modern types
    stylesheet.onload = stylesheet.onreadystatechange = function () {
      //console.log( this.readyState ); //uncomment this line to see which ready states are called.
      // @ts-expect-error - readyState is a legacy IE property not in modern types
      if (!ready && (!this.readyState || this.readyState === 'complete')) {
        ready = true;
        resolve(true);
      }
    };
    headElement.append(stylesheet);
  });
};

// Legacy libraries still loaded via script tags
// These are used by Potree's legacy UI code (jQuery-based panels, controls, etc.)
const scripts = [
  '/potree/libs/jquery/jquery-3.1.1.min.js',
  '/potree/libs/uuid/uuidv4.min.js',
  '/potree/libs/jquery-ui/jquery-ui.min.js',
  '/potree/libs/other/BinaryHeap.js',
  '/potree/libs/tween/tween.min.js',
  '/potree/libs/d3/d3.js',
  '/potree/libs/proj4/proj4.js',
  '/potree/libs/numeric/numeric.js',
  '/potree/libs/openlayers/ol.js',
  '/potree/libs/i18next/i18next.js',
  '/potree/libs/other/stats.js',
];

const styleSheets = [
  // potree.css is now imported via src/potree/index.ts and bundled by Vite
  '/potree/libs/jquery-ui/jquery-ui.min.css',
  '/potree/libs/openlayers/ol.css',
];

const loadScripts = async () => {
  const bodyElement = document.getElementsByTagName('body')[0];
  for (const script of scripts) {
    try {
      await loadScript({ src: script, bodyElement });
    } catch (e) {
      console.error('error loading script', script, e);
      Sentry.captureException(e);
    }
  }
};

export const usePotreeSources = () => {
  const [stylesheetsLoaded, setStyleSheetsLoaded] = useState(false);
  const [scriptsLoaded, setScriptsIsLoaded] = useState(false);

  useEffect(() => {
    // Potree is now imported as source — no need to expose React/ReactDOM/lodash/Apollo
    // as globals since they're resolved by Vite's bundler directly

    const headElement = document.getElementsByTagName('head')[0];
    loadScripts().then(() => {
      setScriptsIsLoaded(true);
    });
    Promise.all(styleSheets.map((src) => loadStyleSheet({ src, headElement }))).then(() => {
      setStyleSheetsLoaded(true);
    });
  }, []);

  return stylesheetsLoaded && scriptsLoaded;
};
